import { addDays, differenceInCalendarDays, format } from 'date-fns'

import { CalendarDay } from './types'

const ISO_DATE = 'yyyy-MM-dd'

// Parse a YYYY-MM-DD string as a local calendar date (avoids the UTC drift of `new Date(iso)`).
const toDate = (iso: string): Date => {
  const [year, month, day] = iso.split('-').map(Number)
  return new Date(year, month - 1, day)
}

export interface CalendarBounds {
  minDate?: string
  maxDate?: string
}

export interface IndexedCalendar extends CalendarBounds {
  byDate: Map<string, CalendarDay>
  // Opt-in only. It must never be inferred from an empty `days` array: callers pass
  // `availability ?? []`, and an availability request that failed also yields [], so inferring it
  // would open every date precisely when we know least about the property.
  isUnrestricted: boolean
}

export const indexCalendar = (
  days: CalendarDay[] = [],
  bounds: CalendarBounds = {},
  isUnrestricted = false,
): IndexedCalendar => ({
  byDate: new Map(days.map(calendarDay => [calendarDay.date, calendarDay])),
  isUnrestricted,
  ...bounds,
})

// Bounds narrow whatever the calendar already allows, so they compose with real availability
// instead of replacing it.
const isWithinBounds = (calendar: IndexedCalendar, date: string): boolean =>
  (!calendar.minDate || date >= calendar.minDate) && (!calendar.maxDate || date <= calendar.maxDate)

// Explicit bounds define the window outright, so the future-only default steps aside for them.
const hasBounds = (calendar: IndexedCalendar): boolean =>
  calendar.minDate != null || calendar.maxDate != null

const todayIso = (): string => format(new Date(), ISO_DATE)

export const canCheckIn = (calendar: IndexedCalendar, date: string): boolean => {
  if (!isWithinBounds(calendar, date)) return false
  if (calendar.isUnrestricted) return hasBounds(calendar) || date >= todayIso()

  const calendarDay = calendar.byDate.get(date)
  return !!calendarDay && calendarDay.checkInAllowed
}

export const canCheckOut = (
  calendar: IndexedCalendar,
  checkInDate: string,
  checkOutDate: string,
): boolean => {
  const nights = differenceInCalendarDays(toDate(checkOutDate), toDate(checkInDate))
  if (nights < 1) return false
  if (!isWithinBounds(calendar, checkInDate) || !isWithinBounds(calendar, checkOutDate)) return false

  if (calendar.isUnrestricted) return hasBounds(calendar) || checkInDate >= todayIso()

  const checkInDay = calendar.byDate.get(checkInDate)
  const checkOutDay = calendar.byDate.get(checkOutDate)
  if (!checkInDay || !checkOutDay) return false
  if (!checkOutDay.checkOutAllowed) return false

  // Every occupied night in [checkIn, checkOut) must exist in the window and allow mid-stay.
  // Walking real dates (not array positions) rejects gaps in the loaded calendar.
  const checkIn = toDate(checkInDate)
  for (let offset = 0; offset < nights; offset += 1) {
    const nightDay = calendar.byDate.get(format(addDays(checkIn, offset), ISO_DATE))
    if (!nightDay || !nightDay.midStayAllowed) return false
  }

  if (checkInDay.minStay != null && nights < checkInDay.minStay) return false
  if (checkInDay.maxStay != null && nights > checkInDay.maxStay) return false

  return true
}

export const isRangeSelectable = (
  calendar: IndexedCalendar,
  checkInDate: string,
  checkOutDate: string,
): boolean => canCheckIn(calendar, checkInDate) && canCheckOut(calendar, checkInDate, checkOutDate)

export const isDateSelectable = (
  calendar: IndexedCalendar,
  date: string,
  checkInDate?: string,
): boolean => (checkInDate ? canCheckOut(calendar, checkInDate, date) : canCheckIn(calendar, date))
